/**
 * 남의 장착 CITY 조회 결과를 기기(localStorage)에 저장한다.
 * 형태: { [userId]: { t: 저장 시각(ms), d: 장착 목록 } }, 환경(environment)별로 따로 보관.
 * CITY가 없다는 결과([])도 저장해 같은 사람을 다시 조회하지 않게 한다.
 */
export const EQUIPPED_STORE_TTL_MS = 24 * 60 * 60 * 1000;
export const EQUIPPED_STORE_MAX_ENTRIES = 300;

const storeKey = (environment) => `city-equipped-store-v1:${environment || 'default'}`;

function defaultStorage() {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch (e) {
    return null;
  }
}

function loadStore(storage, environment) {
  try {
    const parsed = JSON.parse(storage.getItem(storeKey(environment)) || '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (e) {
    return {};
  }
}

function saveStore(storage, environment, store) {
  try {
    storage.setItem(storeKey(environment), JSON.stringify(store));
  } catch (e) {
    // 저장 공간이 없어도 조회·표시에는 영향이 없다.
  }
}

const isFresh = (entry, now) => (
  !!entry && Array.isArray(entry.d) && Number.isFinite(entry.t)
  && entry.t <= now && now - entry.t < EQUIPPED_STORE_TTL_MS
);

/** 24시간 안에 저장한 값이면 { data, timestamp }, 없거나 오래됐으면 null. */
export function readStoredEquipped(environment, userId, { storage = defaultStorage(), now = Date.now() } = {}) {
  if (!storage || !userId) return null;
  const entry = loadStore(storage, environment)[userId];
  return isFresh(entry, now) ? { data: entry.d, timestamp: entry.t } : null;
}

/** 저장하면서 오래된 항목을 정리하고, 최대 개수를 넘으면 오래된 것부터 버린다. */
export function writeStoredEquipped(environment, userId, data, { storage = defaultStorage(), now = Date.now() } = {}) {
  if (!storage || !userId || !Array.isArray(data)) return;
  const kept = Object.entries(loadStore(storage, environment))
    .filter(([id, entry]) => id !== userId && isFresh(entry, now))
    .sort((a, b) => b[1].t - a[1].t)
    .slice(0, EQUIPPED_STORE_MAX_ENTRIES - 1);
  const next = Object.create(null);
  kept.forEach(([id, entry]) => { next[id] = entry; });
  next[userId] = { t: now, d: data };
  saveStore(storage, environment, next);
}

export function removeStoredEquipped(environment, userId, { storage = defaultStorage() } = {}) {
  if (!storage || !userId) return;
  const store = loadStore(storage, environment);
  if (!Object.prototype.hasOwnProperty.call(store, userId)) return;
  delete store[userId];
  saveStore(storage, environment, store);
}
